import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconSearch, IconHelp, IconBulb, IconBulbFilled } from '@tabler/icons-react';
import api from '../services/api';
import { StatusBadge } from '../config/patientStatus';

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export default function TopBar({ providerFilter = null }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [helpHover, setHelpHover] = useState(false);
  const [bulbHover, setBulbHover] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debouncedQuery = useDebounce(query, 300);

  // Fetch results when debounced query changes
  useEffect(() => {
    if (debouncedQuery.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ search: debouncedQuery.trim(), limit: '8' });
    if (providerFilter) params.set('provider_id', providerFilter);
    api.get(`/patients/?${params}`)
      .then(res => {
        if (!cancelled) {
          setResults(res.data || []);
          setOpen(true);
          setActiveIndex(-1);
        }
      })
      .catch(() => { if (!cancelled) setResults([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedQuery, providerFilter]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        inputRef.current && !inputRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const navigateToPatient = useCallback((id) => {
    setQuery('');
    setResults([]);
    setOpen(false);
    setActiveIndex(-1);
    navigate(`/patients/${id}`);
  }, [navigate]);

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && results[activeIndex]) {
        navigateToPatient(results[activeIndex].id);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  const formatName = (p) => {
    return [p.last_name, p.first_name, p.middle_initial ? `${p.middle_initial}.` : null]
      .filter(Boolean)
      .join(', ')
      .replace(/, ([^,]+)$/, ', $1');
  };

  return (
    <div style={styles.topBar}>
      {/* Left — logo/wordmark area (placeholder for now) */}
      <div style={styles.logoArea} />

      {/* Right — search + icons */}
      <div style={styles.rightCluster}>

        {/* Typeahead search */}
        <div style={styles.searchContainer}>
          <div style={styles.searchWrapper}>
            <span style={styles.searchIcon}>
              <IconSearch size={15} stroke={2.5} />
            </span>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => { if (results.length > 0) setOpen(true); }}
              placeholder="Search patients by name or MRN..."
              style={styles.searchInput}
              autoComplete="off"
            />
            {loading && (
              <span style={styles.loadingDot}>
                <span style={styles.spinner} />
              </span>
            )}
          </div>

          {/* Dropdown */}
          {open && (
            <div ref={dropdownRef} style={styles.dropdown}>
              {results.length === 0 ? (
                <div style={styles.dropdownEmpty}>No patients found</div>
              ) : (
                results.map((p, i) => (
                  <div
                    key={p.id}
                    style={{
                      ...styles.dropdownItem,
                      ...(i === activeIndex ? styles.dropdownItemActive : {}),
                    }}
                    onMouseEnter={() => setActiveIndex(i)}
                    onMouseLeave={() => setActiveIndex(-1)}
                    onMouseDown={() => navigateToPatient(p.id)}
                  >
                    <div style={styles.dropdownItemLeft}>
                      <span style={styles.dropdownName}>{formatName(p)}</span>
                      <span style={styles.dropdownMrn}>MRN: {p.mrn}</span>
                    </div>
                    {p.current_status !== 'active' && (
                      <StatusBadge status={p.current_status} />
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Pipe separator */}
        <div style={styles.pipe} />

        {/* Help icon */}
        <button
          style={{
            ...styles.iconBtn,
            ...(helpHover ? styles.iconBtnHelpHover : styles.iconBtnHelp),
          }}
          onMouseEnter={() => setHelpHover(true)}
          onMouseLeave={() => setHelpHover(false)}
          title="Help"
          aria-label="Help"
        >
          <IconHelp size={24} stroke={1.5} />

        </button>

        {/* Pipe separator */}
        <div style={styles.pipe} />

        {/* Feedback icon */}
        <button
          style={{
            ...styles.iconBtn,
            ...(bulbHover ? styles.iconBtnBulbHover : styles.iconBtnBulb),
          }}
          onMouseEnter={() => setBulbHover(true)}
          onMouseLeave={() => setBulbHover(false)}
          title="Share feedback"
          aria-label="Share feedback"
        >
          <IconBulbFilled size={24} stroke={1.5} />
        </button>

      </div>
    </div>
  );
}

const styles = {
  topBar: {
    height: 'var(--top-banner-height)',
    backgroundColor: 'var(--color-bg-page)',
    borderBottom: '1px solid var(--color-border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 var(--space-4)',
    flexShrink: 0,
    boxSizing: 'border-box',
  },
  logoArea: {
    width: '120px',
    flexShrink: 0,
  },
  rightCluster: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
  },
  searchContainer: {
    position: 'relative',
    width: '300px',
  },
  searchWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  searchIcon: {
    position: 'absolute',
    left: '10px',
    color: 'var(--color-text-muted)',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    paddingLeft: '34px',
    paddingRight: '12px',
    paddingTop: '7px',
    paddingBottom: '7px',
    fontSize: 'var(--font-size-sm)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--color-bg-card)',
    color: 'var(--color-text-primary)',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'var(--font-family-base)',
  },
  loadingDot: {
    position: 'absolute',
    right: '10px',
    display: 'flex',
    alignItems: 'center',
  },
  spinner: {
    width: '12px',
    height: '12px',
    border: '2px solid var(--color-border)',
    borderTopColor: 'var(--color-primary)',
    borderRadius: '50%',
    display: 'inline-block',
    animation: 'spin 0.6s linear infinite',
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    backgroundColor: 'var(--color-bg-card)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-lg)',
    zIndex: 500,
    overflow: 'hidden',
  },
  dropdownEmpty: {
    padding: '12px 14px',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-muted)',
    fontFamily: 'var(--font-family-base)',
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    cursor: 'pointer',
    transition: 'background-color var(--transition-fast)',
    borderBottom: '1px solid var(--color-border)',
  },
  dropdownItemActive: {
    backgroundColor: 'var(--color-accent-wash)',
  },
  dropdownItemLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  dropdownName: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-base)',
  },
  dropdownMrn: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    fontFamily: 'var(--font-family-base)',
  },
  pipe: {
    width: '1px',
    height: '20px',
    backgroundColor: 'var(--color-border)',
    flexShrink: 0,
    margin: '0 var(--space-1)',
  },
  iconBtn: {
    background: 'none',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px',
    borderRadius: 'var(--radius-sm)',
    transition: 'color var(--transition-fast), background-color var(--transition-fast)',
  },
  iconBtnHelp: {
    color: 'var(--color-primary)',
  },
  iconBtnHelpHover: {
    color: 'var(--color-primary)',
    backgroundColor: 'var(--color-accent-wash)',
  },
  iconBtnBulb: {
    color: '#C49120',
  },
  iconBtnBulbHover: {
    color: '#C49120',
    backgroundColor: '#FFF8E6',
  },
};